// Univers helpers for the Visits Viewer (frontend).
//
// Since 2026-09 the warehouse table is grained by (visit × univers): a physical
// visit covering both Blanc and Brun is TWO rows, keyed by `universKey`. The
// analysis tabs count those rows — a client watching two univers wants the store
// visit counted in each — so `visits.length` is a number of UNIVERS-VISITS, and
// the number of physical store visits is the count of distinct `visitId`.
//
// Backend twin: backend/utils/visitsSource.js.

// The filter's "no univers chosen" state — it REGROUPS every univers, which is
// exactly what "Tous univers" names. Not a univers value, hence the sentinel.
export const UNIVERS_ALL = '__all__';
export const UNIVERS_ALL_LABEL = 'Tous univers';

/**
 * What the warehouse writes when a visit carries no product univers.
 *
 * The univers is unknown when a visit is PLANNED — you plan a visit to a store, not
 * to an univers — and only recorded when the promoter fills the form. So a missed
 * or cancelled visit is always 'general', and so is every visit of a mono-univers
 * client (Marshall, TH 16215: 24 984 rows, all 'general' — and 141 of the 150 TH
 * projects are in that case).
 *
 * Measured on the two multi-univers projects: Blanc and Brun rows carry a real
 * duration 100 % of the time (6 961/6 961 and 7 673/7 673), 'general' rows only
 * 3.7 % (404 of 10 941 — the rest is 8 584 cancelled and 1 933 planned). Hence
 * "Sans univers" rather than "Tous univers": nothing was recorded, so the visit
 * covers no univers — it does not cover them all.
 */
export const GENERAL_UNIVERS = 'general';

export const universLabel = (univers) =>
  (univers === GENERAL_UNIVERS ? 'Sans univers' : univers || 'Sans univers');

/**
 * A visit's univers, with the warehouse's own convention applied: no product
 * univers means 'general'. Every reader goes through this — when only some did,
 * the donut counted 2 visits where the bar chart counted 3, on the same row.
 */
export const universOf = (visit) => visit?.univers || GENERAL_UNIVERS;

/**
 * The univers present in a set of visits, sorted with 'general' last so the
 * meaningful ones lead the legend and the filter.
 */
export const distinctUnivers = (visits = []) => {
  const seen = [...new Set(visits.map(universOf))];
  return seen.sort((a, b) => {
    if (a === GENERAL_UNIVERS) return 1;
    if (b === GENERAL_UNIVERS) return -1;
    return a.localeCompare(b, 'fr');
  });
};

/**
 * Does this project actually split by univers?
 *
 * One univers means the grain adds nothing to show: every visit is one row and the
 * breakdown would be a single 100 % slice. Those projects keep the summary exactly
 * as it was — no filter, no donut — rather than being handed a chart that says
 * nothing. Two or more is what turns the univers rendering on.
 */
export const hasUniversSplit = (visits = []) => distinctUnivers(visits).length >= 2;

/**
 * Categorical palette for the univers breakdown — validated, not chosen by eye.
 *
 * Both modes are selected steps of the same three hues, not an automatic flip, and
 * both pass every gate on the ALL-PAIRS list against this card's real surfaces
 * (white / gray-800): worst CVD ΔE 9.2 light and 9.4 dark, worst normal-vision ΔE
 * 24.0 / 20.9. All-pairs and not merely adjacent pairs, because any two slices of a
 * donut can end up side by side.
 *
 * THREE IS A CEILING, NOT A COINCIDENCE. The univers vocabulary is client-specific —
 * Blanc/Brun for the CDS accounts, VR/WR for Meta — so a project can have more than
 * three. Every candidate fourth hue was measured and every one fails in dark mode:
 * violet ΔE 1.9 against blue, and yellow, red, magenta and green all below the
 * floors too. So past three the extra univers fold into a neutral "Autres" bucket
 * (the value table still lists each one) rather than cycling back onto slot 1,
 * which would paint two different univers the same color.
 *
 * Hues are assigned by UNIVERS IDENTITY (the fixed `distinctUnivers` order), never
 * by rank: a filter that changes the counts must not repaint the survivors.
 *
 * The light aqua sits at 2.8:1 on white, under the 3:1 bar, so the relief rule
 * applies — hence the value table beside the donut rather than color alone.
 */
export const UNIVERS_PALETTE = {
  light: ['#2a78d6', '#eb6834', '#1baf7a'],
  dark: ['#3987e5', '#d95926', '#199e70'],
};

export const UNIVERS_COLOR_SLOTS = UNIVERS_PALETTE.light.length;

// Neutral, per mode, for the fold bucket — a fold is not another category.
export const OTHER_UNIVERS = { label: 'Autres', light: '#6B7280', dark: '#9CA3AF' };

/**
 * Map univers -> color. Order comes from the population passed in, so pass the
 * UNFILTERED set — hand it a filtered one and the survivors move up the ramp.
 * No modulo: past the validated slots an univers gets the neutral, because
 * cycling would give two univers the same color and make the legend a lie.
 *
 * @param {Array} visits population defining the identity order
 * @param {boolean} dark whether the dark ramp applies
 * @returns {Map<string, string>}
 */
export const universColorMap = (visits = [], dark = false) => {
  const ramp = dark ? UNIVERS_PALETTE.dark : UNIVERS_PALETTE.light;
  const other = dark ? OTHER_UNIVERS.dark : OTHER_UNIVERS.light;
  return new Map(
    distinctUnivers(visits).map((u, i) => [u, i < UNIVERS_COLOR_SLOTS ? ramp[i] : other])
  );
};

export const matchesUnivers = (visit, selected) =>
  !selected || selected === UNIVERS_ALL || universOf(visit) === selected;

/** Number of distinct PHYSICAL visits behind a set of univers-visits. */
export const countStoreVisits = (visits = []) =>
  new Set(visits.map((v) => v.visitId).filter((id) => id !== null && id !== undefined)).size;

/** Univers-visit counts per univers, in `distinctUnivers` order. */
export const countByUnivers = (visits = []) => {
  const counts = new Map(distinctUnivers(visits).map((u) => [u, 0]));
  visits.forEach((v) => {
    const u = universOf(v);
    counts.set(u, (counts.get(u) || 0) + 1);
  });
  return [...counts.entries()].map(([univers, count]) => ({ univers, count }));
};

/** Univers-visit counts per retailer (enseigne), busiest first. */
/**
 * Retailers × univers, ready to stack: one row per retailer carrying a count per
 * univers, in `distinctUnivers` order, busiest retailer first.
 */
export const countByRetailerAndUnivers = (visits = []) => {
  // An absent univers is 'general' — the warehouse's own convention for "no
  // product univers". Normalising here is what guarantees EVERY visit lands in a
  // bucket, so `total` (which sorts the bars) always equals the sum of the
  // segments (which draw them); otherwise a retailer sorts above one with a
  // visibly longer bar, and countByRetailer below loses those visits entirely.
  const order = distinctUnivers(visits);
  const rows = new Map();

  visits.forEach((v) => {
    const key = v.shopSign || 'Sans enseigne';
    if (!rows.has(key)) {
      rows.set(key, { retailer: key, total: 0, byUnivers: new Map(order.map((u) => [u, 0])) });
    }
    const row = rows.get(key);
    const u = universOf(v);
    row.total += 1;
    row.byUnivers.set(u, (row.byUnivers.get(u) || 0) + 1);
  });

  return {
    univers: order,
    retailers: [...rows.values()]
      .sort((a, b) => b.total - a.total || a.retailer.localeCompare(b.retailer, 'fr')),
  };
};

/**
 * A duration column as a number, or null when nothing was measured.
 *
 * Apart from a truthiness test so a real 0 survives, and from parseInt so 20.9
 * stays 20.9. `Number()` alone is the trap: it maps null and '' onto 0, and a
 * missing duration must never arrive as a measured zero.
 */
export const minutes = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Which series holds the first and last visible segment of each stacked bar.
 *
 * Per BAR, not per series: FNAC does a Brun-only round, so its Blanc segment is
 * zero-width. Rounding series 0 unconditionally would round that invisible
 * segment and leave the drawn bar square at its left end.
 *
 * `series` is one array of values per series, each indexed by bar.
 */
export const stackEnds = (barCount, series = []) =>
  Array.from({ length: barCount }, (_, bar) => {
    const filled = series
      .map((values, index) => ((values[bar] || 0) > 0 ? index : -1))
      .filter((index) => index >= 0);
    return { first: filled[0], last: filled[filled.length - 1] };
  });

/**
 * Visits paired with the minutes to read for them: the univers-allocated share.
 *
 * FCT_VISITS repeats the SAME `ACTUAL_DURATION_MINUTES` on every univers row of a
 * physical visit (measured on 4 389/4 389 TH multi-row visits), so that column
 * answers no question at the univers grain. `DURATION_MINUTES_ALLOCATED` is the
 * row's share, and the shares sum back to the real duration exactly.
 *
 * Allocated on EVERY lens, "Tous univers" included. Reading the physical duration
 * there made retailers incomparable: FNAC, which only does Brun, showed 37 min
 * against BOULANGER's 1h44 for Blanc+Brun — a 3× gap that is mostly a difference
 * in univers count, not in work. Per univers the same two read 37 and 52.
 *
 * A duration TARGET is a different question and keeps the physical duration:
 * see physicalVisitDurations().
 */
export const visitsWithDuration = (visits = [], selectedUnivers = UNIVERS_ALL) => {
  // Number.isFinite, not a truthiness test: a measured 0 is an observation
  // ("nothing went into Brun on this visit") and dropping it biases the average
  // upward, while NaN — which a bare Number() upstream can produce — must go.
  const usable = (m) => Number.isFinite(m);

  return visits
    .filter((v) => matchesUnivers(v, selectedUnivers))
    // Falls back on anything unusable, not just null: `??` let a NaN allocation
    // through, and the finiteness filter below then dropped the visit entirely
    // instead of reading its real duration.
    .map((v) => ({
      ...v,
      durationMinutes: Number.isFinite(v.allocatedDurationMinutes)
        ? v.allocatedDurationMinutes
        : v.actualDurationMinutes,
    }))
    .filter((v) => usable(v.durationMinutes));
};

/**
 * One row per PHYSICAL visit, carrying the real duration.
 *
 * For comparisons against a per-store duration target: those targets run 45 to
 * 180 minutes and describe how long a visit should last, so measuring them
 * against one univers' share would flag every multi-univers store as under.
 */
export const physicalVisitDurations = (visits = []) => {
  const seen = new Set();
  return visits
    .filter((v) => {
      // A row with no visit id cannot be deduplicated against anything; collapsing
      // them all into one would silently delete real work.
      if (v.visitId === null || v.visitId === undefined) return true;
      if (seen.has(v.visitId)) return false;
      seen.add(v.visitId);
      return true;
    })
    .map((v) => ({ ...v, durationMinutes: v.actualDurationMinutes }))
    .filter((v) => Number.isFinite(v.durationMinutes));
};

/**
 * The average minutes per univers, in `distinctUnivers` order — the detail behind
 * a "Tous univers" average, so a reader can see which univers pulled it.
 */
export const averageDurationByUnivers = (visits = []) => {
  const acc = new Map(distinctUnivers(visits).map((u) => [u, { total: 0, count: 0 }]));
  visitsWithDuration(visits, UNIVERS_ALL).forEach((v) => {
    const bucket = acc.get(universOf(v));
    if (!bucket) return;
    bucket.total += v.durationMinutes;
    bucket.count += 1;
  });
  return [...acc.entries()]
    .filter(([, b]) => b.count > 0)
    .map(([univers, b]) => ({ univers, minutes: Math.round(b.total / b.count), count: b.count }));
};
